import type { MissionOverviewRangeSettings } from "lib/fleetMissionStorage/missionOverviewRange";
import { resolveMissionOverviewDateRange } from "lib/fleetMissionStorage/missionOverviewRange";

const RANGE_OPTIONS = [
    ["allTime", "All time"],
    ["thisMonth", "This Month"],
    ["thisWeek", "This Week"],
    ["custom", "Custom"],
] as const;

const compactDateRange = (startDate: string, endDate: string): string => {
    const compact = (value: string) => value.slice(5).replace("-", "/");
    return `${compact(startDate)}–${compact(endDate)}`;
};

type PresetDateRangeInputProps = {
    value: MissionOverviewRangeSettings;
    onChange: (value: MissionOverviewRangeSettings) => void;
    className?: string;
};

const PresetDateRangeInput = ({ value, onChange, className = "" }: PresetDateRangeInputProps) => {
    const dateRange = resolveMissionOverviewDateRange(value);
    const customRangeInvalid = value.selection === "custom" && dateRange === null;

    return (
        <div className={`flex flex-col items-end gap-1 ${className}`}>
            <div className="flex flex-wrap items-center justify-end gap-2">
                <div
                    aria-label="Mission date range"
                    className="flex h-10 items-center overflow-visible rounded-full border border-black70 bg-black90"
                    role="group"
                >
                    {RANGE_OPTIONS.map(([selection, label]) => {
                        const selected = value.selection === selection;
                        const showsWeekRange = selection === "thisWeek" && selected && dateRange;
                        return (
                            <button
                                key={selection}
                                aria-pressed={selected}
                                className={`text-h5 relative flex h-full items-center px-3 transition-colors first:rounded-l-full last:rounded-r-full ${
                                    selected
                                        ? "bg-primary2 text-white"
                                        : "text-black40 hover:bg-black80 hover:text-white"
                                }`}
                                type="button"
                                onClick={() => onChange({ ...value, selection })}
                            >
                                <span>{label}</span>
                                {showsWeekRange && (
                                    <span
                                        className="text-black30 absolute bottom-px left-1/2 -translate-x-1/2 whitespace-nowrap text-[8px] leading-none"
                                        title={`${dateRange.startDate} – ${dateRange.endDate}`}
                                    >
                                        {compactDateRange(dateRange.startDate, dateRange.endDate)}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>
                {value.selection === "custom" && (
                    <div className="text-h5 flex items-center gap-1 text-black40">
                        <input
                            aria-label="Mission range start date"
                            className="h-8 rounded-r5 border border-black70 bg-black90 px-2 text-white outline-none focus:border-primary2"
                            max={value.customEndDate || undefined}
                            type="date"
                            value={value.customStartDate}
                            onChange={(event) =>
                                onChange({ ...value, customStartDate: event.target.value })
                            }
                        />
                        <span>to</span>
                        <input
                            aria-label="Mission range end date"
                            className="h-8 rounded-r5 border border-black70 bg-black90 px-2 text-white outline-none focus:border-primary2"
                            min={value.customStartDate || undefined}
                            type="date"
                            value={value.customEndDate}
                            onChange={(event) =>
                                onChange({ ...value, customEndDate: event.target.value })
                            }
                        />
                    </div>
                )}
            </div>
            {customRangeInvalid && (
                <p className="text-h5 text-warning" role="alert">
                    Select a valid inclusive start and end date.
                </p>
            )}
        </div>
    );
};

export default PresetDateRangeInput;
